/**
 * /hypnose/regressive
 * Page pédagogique : le terme est très recherché, mais chez Parfaits Imparfaits
 * ce n'est PAS une prestation distincte — c'est un outil intégré à l'hypnose
 * spirituelle. Cette page clarifie le positionnement.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Seo } from "../../components/Layout";
import { PageHero, ProseSection, EndCTA } from "../../components/PageParts";

export default function Regressive() {
  const BREADCRUMB = [
    { to: "/", label: "Accueil" },
    { to: "/hypnose", label: "L'Hypnose" },
    { label: "Hypnose régressive" },
  ];
  return (
    <>
      <Seo
        title="Hypnose régressive : ce qu'elle est vraiment"
        description="Qu'est-ce que l'hypnose régressive ? Comment se distingue-t-elle de l'hypnose ericksonienne et spirituelle ? Un point clair, sans mystification, par Valentine DL, praticienne à Pantin."
        canonical="https://www.parfaitsimparfaits.fr/hypnose/regressive"
      />

      <PageHero
        breadcrumb={BREADCRUMB}
        eyebrow="Point de repère"
        title="L'hypnose régressive,"
        italic="qu'est-ce que c'est vraiment ?"
        subtitle="Un terme fréquemment recherché, autour duquel les définitions varient. Voici comment je la comprends, et comment je l'intègre dans ma pratique."
      />

      <ProseSection>
        <h2 className="font-serif text-3xl text-sand-900 !mt-0">Une définition simple</h2>
        <p>
          L'hypnose régressive désigne un <strong className="text-sand-900 font-medium">travail
          d'exploration de la mémoire</strong>. Elle invite la personne, dans un état modifié
          de conscience, à revisiter certains souvenirs — parfois anciens, parfois enfouis — pour
          en comprendre l'empreinte et en libérer la charge émotionnelle.
        </p>
        <p>
          Selon les écoles, elle est présentée comme le retour vers un événement précis de l'enfance
          (hypnose régressive classique), l'exploration de « vies antérieures », ou l'intégration de
          « mémoires ancestrales ». Chaque école a son vocabulaire, ses métaphores et ses
          finalités&nbsp;: il n'existe pas <em>une</em> hypnose régressive, mais <em>plusieurs</em>.
        </p>

        <h2 className="font-serif text-3xl text-sand-900">Ma manière de l'aborder</h2>
        <p>
          Chez Parfaits Imparfaits, l'hypnose régressive n'est <strong className="text-sand-900 font-medium">pas
          une prestation à part</strong>. C'est un <strong className="text-sand-900 font-medium">outil</strong>{" "}
          qui peut s'intégrer naturellement à une séance d'hypnose spirituelle,
          lorsque le fil du travail l'appelle.
        </p>
        <p>
          Je l'utilise avec <strong className="text-sand-900 font-medium">mesure et discernement</strong>&nbsp;:
          non pas pour aller chercher du spectaculaire, mais pour permettre d'accueillir ce qui, dans
          votre histoire ou vos ressentis, cherche à être vu. Ce que la personne explore lui appartient
          entièrement — je ne cherche jamais à interpréter à sa place ni à vendre du sensationnel.
        </p>

        <h2 className="font-serif text-3xl text-sand-900">Comment savoir si c'est pour vous&nbsp;?</h2>
        <p>
          Si vous ressentez qu'un thème revient depuis longtemps, qu'un schéma se rejoue sans que
          vous compreniez pourquoi, ou qu'un poids ancien vous accompagne&nbsp;: c'est souvent un
          bon indicateur qu'un travail intégrant une dimension régressive pourrait vous être utile.
        </p>
        <p>
          Ce type de travail s'inscrit dans le cadre d'une{" "}
          <Link to="/hypnose/spirituelle" className="text-[#B07A45] underline hover:text-sand-900">
            séance d'hypnose spirituelle
          </Link>
          , d'une durée d'environ 2h.
        </p>

        <div className="mt-12 rounded-2xl bg-sand-200/40 border border-sand-300/60 p-8">
          <p className="font-serif italic text-xl text-sand-900 leading-relaxed">
            « L'hypnose régressive n'a pas besoin d'être extraordinaire pour être juste.
            Elle est utile quand elle nous aide à comprendre — et à avancer. »
          </p>
        </div>

        <p className="!mt-10 text-center">
          <Link
            to="/hypnose/spirituelle"
            data-testid="regressive-cta-spirituelle"
            className="btn-primary"
          >
            Découvrir la séance d'hypnose spirituelle
          </Link>
        </p>
      </ProseSection>

      <EndCTA
        title="Une question précise&nbsp;?"
        text="Je vous répondrai personnellement pour vous orienter, sans engagement."
        testid="regressive-end-cta"
      />
    </>
  );
}
